import { useCallback, useMemo } from "react"

import { useMapStateContext, Map, BASE_MAP_ID, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { GpsLocationPinMarker } from "../../components/GpsLocationPinMarker"
import type { RoadSearchValue } from "../../components/RoadSearch"
import { useNetworksDispatchContext } from "../../contexts/NetworksContext"
import { formatToBoundingBox } from "../../utils/formatToBoundingBox"

import { RoadSearch } from "../RoadSearch"

export function MapWithSearchBar(props) {

    const {
        children,
        roadSearchClosed = false,
        displayGpsLocationPin = true,
        id = BASE_MAP_ID,
        module,
        onRoadSelected,
        subNetworks,
        ...mapProps
    } = props

    const isMobile = useIsMobileDevice()
    const { mapPin } = useMapStateContext()
    const mapPinLocation = mapPin?.[id]
    const { searchRoadCallback } = useMapStateContext()
    const { getMapRef, setMapBoundingBoxToFit, setMapPin } = useMapDispatchContext()
    const { setRoadSearched } = useNetworksDispatchContext()

    const onRoadSearched = useCallback(
        (inputValue: RoadSearchValue) => {
            setMapPin(id)
            if (inputValue.value) {

                if (inputValue.type === "road") {
                    setRoadSearched(inputValue.value)
                    if (onRoadSelected) {
                        onRoadSelected(inputValue.value)
                    } else {
                        searchRoadCallback?.(inputValue.value)
                        setRoadSearched(inputValue.value)
                    }
                }

                if (inputValue.type === "gps") {
                    setRoadSearched(inputValue.value.join(","))
                    const gpsValue = inputValue
                    const map = getMapRef(id)?.getMap()
                    if (map) {
                        const [lng, lat] = gpsValue.value
                        const bboxToFit = formatToBoundingBox(lat, lng)
                        setMapBoundingBoxToFit(
                            bboxToFit,
                            Math.max(map.getZoom()),
                            id,
                        )
                        if (displayGpsLocationPin) {
                            setMapPin(id, [lat, lng])
                        }
                        return
                    }
                }
            }
        }, [displayGpsLocationPin, getMapRef, id, onRoadSelected, searchRoadCallback, setMapBoundingBoxToFit, setMapPin, setRoadSearched])

    const TopLeftElements = useMemo(() => {
        const otherTopLeftElements = props.topLeftElements?.length ? props.topLeftElements : null

        if (!isMobile) {
            return [<RoadSearch
                key="road-search"
                disableExpend={isMobile ? false : !roadSearchClosed}
                module={module}
                onRoadSearched={onRoadSearched}
                subNetworks={subNetworks}
            />, otherTopLeftElements]
        } else {
            return otherTopLeftElements
        }
    }, [onRoadSearched, isMobile, module, props.topLeftElements, roadSearchClosed, subNetworks])

    const TopRightElements = useMemo(() => {
        const otherTopRightElements = props.topRightElements?.length ? props.topRightElements : null

        if (!isMobile) {
            return otherTopRightElements
        } else {
            return [<RoadSearch
                key="road-search"
                disableExpend={isMobile ? false : !roadSearchClosed}
                module={module}
                onRoadSearched={onRoadSearched}
                subNetworks={subNetworks}
            />, otherTopRightElements]
        }
    }, [onRoadSearched, isMobile, module, props.topRightElements, roadSearchClosed, subNetworks])

    return <Map
        {...mapProps}
        id={id}
        topLeftElements={TopLeftElements}
        topRightElements={TopRightElements}
    >
        {mapPinLocation && <GpsLocationPinMarker location={mapPinLocation} />}
        {children}
    </Map>
}